<script setup name="TvbHomeColumnsNews">
import request from '@/utils/request'
import { reactive, inject, onServerPrefetch } from 'vue'
import { action } from './../../../../utils/gtm'
import { checkVideo, toReativeTime } from './../../../../utils/index'

const size = inject('size')
const props = defineProps({
  info: {
    type: Object,
    default: null
  },
  linkTarget: {
    type: Number,
    default: 1
  },
  isVideo: {
    type: Boolean,
    default: false
  }
})
const news = reactive({
  list: [],
  moreUrl: ''
})

const fetchData = async () => {
  if (props.info.api) {
    const { data } = await request({
      url: props.info.api,
      method: 'get'
    })
    let { list, more_url } = data
    news.list = list
    news.moreUrl = more_url
  }
}
fetchData()
onServerPrefetch(fetchData)

const title = (item) => item.title || item.post_content
// item?.post_tags?.[0] ||
const tag = (item) => item.category_title || item.category_name
const datetime = (item) => item?.publish_datetime || item?.post_datetime || item?.publish_at
const tracking = (item) => {
  action({
    page_title: props.info.title,
    screen_class: title(item),
    screenID: 'S001',
    eventCategory: 'home_banner',
    eventLabel: props.info.api,
    eventID: item.id || item.post_id || 'B001',
    screenLabel: item.jump_url || item.link
  })
}
</script>

<template>
  <section class="tvb-home-columns-news" :class="[size]">
    <header class="tvb-home-columns-news-header">
      <h2 class="tvb-home-columns-news-title">
        <component
          :is="news?.moreUrl ? 'tvb-router-link' : 'span'"
          :link-target="linkTarget"
          :to="news?.moreUrl"
        >
          {{ info.title }}
        </component>
      </h2>
      <!-- && news.list.length > ['s', 'xs'].includes(size) ? 5 : 9 -->
      <tvb-button
        v-if="news.moreUrl"
        :style="{ '--tvb-button-height': ['s', 'xs'].includes(size) ? '24px' : '36px' }"
        class="tvb-home-columns-news_more"
        :type="!['s', 'xs'].includes(size) ? 'more' : 'more-text'"
        :animation="!['s', 'xs'].includes(size) ? 'more' : ''"
        icon="icon_right_more"
        :path="news.moreUrl"
        :link-target="linkTarget"
        >查看更多</tvb-button
      >
    </header>
    <main class="tvb-home-columns-news-main">
      <div class="tvb-home-columns-news-main-four">
        <TvbRouterLink
          v-for="(item, index) in news.list.slice(0, ['s', 'xs'].includes(size) ? 1 : 4)"
          :key="index"
          :to="item.jump_url || item.link"
          class="tvb-home-columns-news-main-card tvb-hairline--surround"
          @click="tracking(item)"
          :link-target="item.link_target || linkTarget"
        >
          <TvbImage
            :src="item.thumbnails || item.image_info"
            class="tvb-home-columns-news-main-card-image"
            :is-video="isVideo || checkVideo(item)"
            :play-icon="{ size: { xl: 48, l: 48, m: 48, s: 40, xs: 40 } }"
            backgroundc="#F5F6F7"
            :border-radius="['s', 'xs'].includes(size) ? 16 : 16"
            transform
            :alt="title(item)"
          />
          <div class="tvb-home-columns-news-main-card-info">
            <h3 class="tvb-home-columns-news-main-title" :title="title(item)">
              <span>{{ title(item) }}</span>
            </h3>
            <p class="tvb-home-columns-news-main-tag">
              <span v-if="tag(item)" :title="tag(item)">{{ tag(item) }}</span>
              <time v-if="datetime(item)" :datetime="datetime(item) * 1000">
                {{ toReativeTime(datetime(item)) }}
              </time>
            </p>
          </div>
        </TvbRouterLink>
      </div>
      <div class="tvb-home-columns-news-main-five">
        <TvbRouterLink
          v-for="(item, index) in news.list.slice(
            ['s', 'xs'].includes(size) ? 1 : 4,
            ['s', 'xs'].includes(size) ? 6 : 9
          )"
          :key="index"
          :to="item.jump_url || item.link"
          :link-target="item.link_target || linkTarget"
          class="tvb-home-columns-news-main-card"
          @click="tracking(item)"
        >
          <TvbImage
            :src="item.thumbnails || item.image_info"
            class="tvb-home-columns-news-main-card-image"
            :is-video="isVideo || checkVideo(item)"
            transform
            border
            :play-icon="{ size: { xl: 48, l: 48, m: 48, s: 32, xs: 32 } }"
            :key="item.id || index"
            :border-radius="['s', 'xs'].includes(size) ? 12 : 16"
            :alt="title(item)"
          />
          <div class="tvb-home-columns-news-main-card-info">
            <h3 class="tvb-home-columns-news-main-card-title" :title="title(item)">
              <span>{{ title(item) }}</span>
            </h3>
            <p class="tvb-home-columns-news-main-tag">
              <span v-if="tag(item)" :title="tag(item)">{{ tag(item) }}</span>
              <time v-if="datetime(item)" :datetime="datetime(item) * 1000">
                {{ toReativeTime(datetime(item)) }}
              </time>
            </p>
          </div>
        </TvbRouterLink>
      </div>
    </main>
    <footer
      class="tvb-home-columns-news-footer"
      v-if="['s', 'xs'].includes(size) && news.list.length > 5 && news.moreUrl"
    >
      <tvb-button
        type="big-more"
        animation=""
        icon="icon_right_more"
        :path="news.moreUrl"
        :link-target="linkTarget"
      >
        查看更多
      </tvb-button>
    </footer>
  </section>
</template>

<style lang="scss" scoped>
.tvb-home-columns-news {
  min-width: 320px;
  --tvb-home-columns-border-radius: 16px;
  .tvb-home-columns-news-header {
    display: flex;
    justify-content: space-between;
    .tvb-home-columns-news-title {
      font-size: 22px;
      font-weight: 500;
      color: #231f20;
      line-height: 36px;
      letter-spacing: 0.5px;
      > a {
        text-decoration: none;
        color: inherit;
        @include text-hover-before();
        &:hover {
          @include text-hover-active();
        }
      }
    }
    :deep(.tvb-home-columns-news_more) {
      &.btn-more .svg-icon {
        margin-left: 1px;
      }
    }
  }

  .tvb-home-columns-news-main {
    display: flex;
    margin-top: 16px;
    --tvb-home-columns-border-radius: 16px;
    .tvb-home-columns-news-main-four {
      width: calc((100% - 16px * 2) / 3 * 2 + 16.02px);
      margin-right: 8px;
      display: flex;
      flex-wrap: wrap;

      .tvb-home-columns-news-main-card {
        display: block;
        width: calc((100% - 16px) / 2);
        text-decoration: none;
        background: #fcfcfc;
        border-radius: var(--tvb-home-columns-border-radius);
        overflow: hidden;
        --tvb-hairline-border-radius: calc(2 * var(--tvb-home-columns-border-radius));

        .tvb-home-columns-news-main-card-image {
          width: 100%;
          height: auto;
          aspect-ratio: 1/0.5625;
          overflow: hidden;
          border-radius: var(--tvb-home-columns-border-radius);
        }

        .tvb-home-columns-news-main-card-info {
          padding: 10px 16px 12px;

          .tvb-home-columns-news-main-title {
            font-size: 18px;
            font-weight: 500;
            color: #231f20;
            line-height: 26px;
            height: 52px;
            letter-spacing: 0.5px;

            @include line-text-ellipsis(2);
            span {
              @include text-hover-before();
              &:hover {
                @include text-hover-active();
              }
            }
          }

          .tvb-home-columns-news-main-tag {
            margin-top: 4px;
            font-size: 16px;
            font-weight: 400;
            color: #818d95;
            line-height: 24px;
            display: flex;
            align-items: center;
            letter-spacing: 0.5px;
            white-space: nowrap;

            span {
              display: inline-block;
              margin-right: 12px;
              white-space: nowrap;
              max-width: 60%;
              overflow: hidden;
              text-overflow: ellipsis;
            }

            time {
              white-space: nowrap;
            }
          }
        }

        &:nth-child(1) {
          margin-right: 8px;
          margin-bottom: 8px;
        }
        &:nth-child(2) {
          margin-left: 8px;
          margin-bottom: 8px;
        }
        &:nth-child(3) {
          margin-right: 8px;
          margin-top: 8px;
        }
        &:nth-child(4) {
          margin-left: 8px;
          margin-top: 8px;
        }
      }
    }
    .tvb-home-columns-news-main-five {
      width: calc((100% - 16px * 2) / 3 * 1);
      margin-left: 8px;
      .tvb-home-columns-news-main-card {
        width: 100%;
        height: calc((100% - 16px * 4) / 5 * 1);
        display: flex;
        text-decoration: none;
        margin-top: 16px;
        overflow: hidden;
        &:first-child {
          margin-top: 0;
        }
        .tvb-home-columns-news-main-card-image {
          width: calc((100% - var(--tvb-home-columns-border-radius)) / 2 + 5.02px);
          margin-right: 6px;
          // height: auto;
          aspect-ratio: 285.57/160.8;
          border-radius: var(--tvb-home-columns-border-radius);
          overflow: hidden;
        }

        .tvb-home-columns-news-main-card-info {
          width: calc((100% - var(--tvb-home-columns-border-radius)) / 2 - 5.02px);
          // margin-left: 2px;
          padding-left: 10px;
          .tvb-home-columns-news-main-card-title {
            font-size: 18px;
            font-weight: 500;
            color: #231f20;
            line-height: 26px;
            max-height: 78px;
            overflow: hidden;
            letter-spacing: 0.5px;

            @include line-text-ellipsis(3);
            span {
              @include text-hover-before();
              &:hover {
                @include text-hover-active();
              }
            }
          }

          .tvb-home-columns-news-main-tag {
            margin-top: 4px;
            font-size: 16px;
            font-weight: 400;
            color: #818d95;
            line-height: 24px;
            white-space: nowrap;
            display: flex;
            align-items: center;
            letter-spacing: 0.5px;
            span {
              margin-right: 12px;
              white-space: nowrap;
              white-space: nowrap;
              display: inline-block;
              max-width: 50%;
              overflow: hidden;
              text-overflow: ellipsis;
            }
          }
        }
      }
    }
  }

  .tvb-home-columns-news-footer {
    display: none;
  }

  &.s {
    .tvb-home-columns-news-main {
      padding: 0px 16px !important;
    }
    .tvb-home-columns-news-footer {
      padding: 16px 16px 0 !important;
    }
  }
  &.xs {
    .tvb-home-columns-news-main {
      padding: 0px 12px !important;
    }
    .tvb-home-columns-news-footer {
      padding: 16px 12px 0 !important;
    }
  }
  &.s,
  &.xs {
    --tvb-home-columns-border-radius: 12px;
    .tvb-home-columns-news-header {
      padding: 0 12px;
      align-items: center;
      .tvb-home-columns-news-title {
        font-size: 17px;
        font-weight: 500;
        color: #231f20;
        line-height: 24px;
      }
    }
    .tvb-home-columns-news-main {
      flex-direction: column;
      margin-top: 10px;
      .tvb-home-columns-news-main-four {
        width: 100%;
        .tvb-home-columns-news-main-card {
          width: 100%;
          margin: 0;
          border-radius: var(--tvb-home-columns-border-radius);

          --tvb-hairline-border-radius: calc(2 * var(--tvb-home-columns-border-radius));

          .tvb-home-columns-news-main-card-image {
            aspect-ratio: 1 / 0.5628415300546448;
            border-radius: var(--tvb-home-columns-border-radius);

            @supports not (aspect-ratio: 1 / 0.5628415300546448) {
              height: 196px;
            }
          }

          .tvb-home-columns-news-main-card-info {
            padding: 8px 12px 12px;
            .tvb-home-columns-news-main-title {
              font-size: 16px;
              font-weight: 400;
              color: #231f20;
              line-height: 24px;
              height: 24px;
              overflow: hidden;

              @include line-text-ellipsis(2);
            }

            .tvb-home-columns-news-main-tag {
              font-size: 13px;
              font-weight: 400;
              color: #818d95;
              line-height: 18px;
              letter-spacing: 0;
              span {
                margin-right: 10px;
              }
            }
          }
        }
      }
      .tvb-home-columns-news-main-five {
        width: 100%;
        margin: 0;
        .tvb-home-columns-news-main-card {
          width: 100%;
          margin: 0;
          margin-top: 16px;

          .tvb-home-columns-news-main-card-image {
            width: auto;
            height: 72px;
            aspect-ratio: 1 / 0.5625;
            border-radius: var(--tvb-home-columns-border-radius);
            margin-right: 5px;
            @supports not (aspect-ratio: 1 / 0.5625) {
              width: 127px;
            }
          }

          .tvb-home-columns-news-main-card-info {
            padding-left: 7px;
            width: calc(100vw - 32.82051282051282vw - 7px - 12px * 2);
            .tvb-home-columns-news-main-card-title {
              font-size: 16px;
              font-weight: 400;
              color: #231f20;
              line-height: 24px;
              max-height: 48px;
            }

            .tvb-home-columns-news-main-tag {
              font-size: 13px;
              font-weight: 400;
              color: #818d95;
              line-height: 18px;
              margin-top: 6px;
              letter-spacing: 0;
              span {
                margin-right: 10px;
              }
            }
          }
        }
      }
    }
    .tvb-home-columns-news-footer {
      display: block;
      padding: 16px 12px 0;
    }
  }
  &.s {
    .tvb-home-columns-news-header {
      padding: 0 16px;
    }
  }

  &.l,
  &.m {
    .tvb-home-columns-news-header {
      .tvb-home-columns-news-title {
        font-size: 20px;
      }
    }
  }
}
</style>
